/**
 * Public restaurant links built from already-loaded tenant configuration.
 * Social URLs stay as the tenant supplied them when they are absolute HTTPS.
 * Map links are constructed here because the storefront has no maps provider.
 * This does not geocode and does not call a maps API.
 */

export type RestaurantAddressParts = {
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string | null;
};

export type RestaurantMapLinks = {
  viewOnMap: string;
  directions: string;
};

function hasControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code <= 31 || code === 127) return true;
  }
  return false;
}

const MAP_SEARCH_URL = "https://www.google.com/maps/search/";
const MAP_DIRECTIONS_URL = "https://www.google.com/maps/dir/";

function isFiniteInRange(value: number | null, min: number, max: number): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= min && value <= max;
}

/** Absolute HTTPS only. Rejects other schemes and malformed input. Does not rewrite the URL. */
export function resolveHttpsUrl(raw: string | null | undefined): string | null {
  const trimmed = raw?.trim() ?? "";
  if (!trimmed || hasControlCharacter(trimmed)) return null;
  if (trimmed.startsWith("//")) return null;

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }

  if (url.protocol !== "https:") return null;
  return trimmed;
}

function trimmedPart(value: string | null): string {
  return value?.trim() ?? "";
}

/** Display lines: street, locality, country. Empty parts are omitted. */
export function formatRestaurantAddressLines(parts: RestaurantAddressParts): string[] {
  const street = trimmedPart(parts.address);
  const locality = [parts.city, parts.state, parts.zip_code]
    .map((part) => trimmedPart(part))
    .filter((part) => part.length > 0)
    .join(", ");
  const country = trimmedPart(parts.country);
  return [street, locality, country].filter((line) => line.length > 0);
}

/** Single display line from the structured storefront address. Null when nothing usable remains. */
export function formatRestaurantAddress(parts: RestaurantAddressParts): string | null {
  const line = [parts.address, parts.city, parts.state, parts.zip_code, parts.country]
    .map((part) => part?.trim() ?? "")
    .filter((part) => part.length > 0)
    .join(", ");
  return line.length > 0 ? line : null;
}

/**
 * Prefer a complete coordinate pair. Otherwise use the formatted address as the destination.
 * Returns null when neither is usable.
 */
export function resolveRestaurantMapLinks(
  parts: RestaurantAddressParts & { latitude: number | null; longitude: number | null }
): { addressLine: string | null; links: RestaurantMapLinks | null } {
  const addressLine = formatRestaurantAddress(parts);
  const latitude = isFiniteInRange(parts.latitude, -90, 90) ? parts.latitude : null;
  const longitude = isFiniteInRange(parts.longitude, -180, 180) ? parts.longitude : null;
  const coordinateQuery =
    latitude != null && longitude != null ? `${latitude},${longitude}` : null;
  const query = coordinateQuery ?? addressLine;
  if (!query) return { addressLine, links: null };

  const view = new URL(MAP_SEARCH_URL);
  view.searchParams.set("api", "1");
  view.searchParams.set("query", query);

  const directions = new URL(MAP_DIRECTIONS_URL);
  directions.searchParams.set("api", "1");
  directions.searchParams.set("destination", query);

  return {
    addressLine,
    links: {
      viewOnMap: view.toString(),
      directions: directions.toString(),
    },
  };
}
